// Universal Scripting
// ================================
// ================================
// Media
// ================================

// Custom video button - scoped to closest video in the same parent element
document.addEventListener('click', function (e) {
    const btn = e.target.closest('.rev-play');
    if (!btn) return;
    const wrap = btn.closest('.rev-media');
    if (!wrap) return;
    const video = wrap.querySelector('video');
    if (!video) return;
    if (!video.hasAttribute('controls')) video.setAttribute('controls', 'controls');
    try { video.muted = false; } catch (_) { }
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* ignore autoplay-policy errors */ });
    btn.style.display = 'none';
    video.addEventListener('ended', function handleEnd() {
        video.removeAttribute('controls');
        btn.hidden = false;
        video.currentTime = 0;
        video.pause();
        video.removeEventListener('ended', handleEnd);
    }, { once: true });
}, { passive: true });

// ================================
// Generic Partial Rerender
// Can rerender anything as long as:
// - server HTML contains the same selectors
// - caller provides a URL
// ================================

const RevRerender = (() => {
    const abortMap = new WeakMap();

    async function rerender({
        contextEl,
        targets = [],
        url,
        params = {},
        incomingScopeSelector = null,
    }) {
        if (!contextEl || !targets.length) return;

        const controller = new AbortController();
        abortMap.get(contextEl)?.abort();
        abortMap.set(contextEl, controller);

        const finalUrl = buildUrl(url, params);
        if (!finalUrl) return;

        const res = await fetch(finalUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: controller.signal
        });
        if (!res.ok) return;

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const incomingRoot = incomingScopeSelector
            ? doc.querySelector(incomingScopeSelector)
            : doc;

        if (!incomingRoot) return;

        targets.forEach(selector => {
            const current = contextEl.querySelector(selector);
            if (!current) return;

            const incoming = incomingRoot.querySelector(selector);
            if (!incoming) return;

            current.replaceWith(incoming);
        });
    }

    function buildUrl(url, params) {
        if (!url) return null;

        if (typeof url === 'function') return url(params);

        const u = new URL(url, window.location.origin);
        Object.entries(params).forEach(([k, v]) => {
            if (v != null) u.searchParams.set(k, v);
        });
        return u.toString();
    }

    return { rerender };
})();

// ================================
// Variant Selector
// ================================

// Active Class Change
document.addEventListener('change', function (e) {
    if (e.target.type === 'radio' && e.target.closest('.rev-variant-selector')) {
        const radioInput = e.target;
        const variantSelector = radioInput.closest('.rev-variant-selector');
        const radioName = radioInput.name;

        const relatedSwatches = variantSelector.querySelectorAll(
            `input[type="radio"][name="${radioName}"]`
        );

        relatedSwatches.forEach(function (radio) {
            const swatchOption = radio.closest('.rev-swatch-option');
            if (swatchOption) {
                swatchOption.classList.remove('rev-option-active');
            }
        });

        const selectedSwatchOption = radioInput.closest('.rev-swatch-option');
        if (selectedSwatchOption) {
            selectedSwatchOption.classList.add('rev-option-active');
        }
    }
});

// Change ATC Variant ID
document.addEventListener('change', function (e) {
    const selector = e.target.closest('.rev-variant-selector');
    if (!selector) return;

    const card = selector.closest('.rev-product-card');
    if (!card) return;

    const script = card.querySelector('.rev-variants-json');
    if (!script) return;

    let variants;
    try {
        variants = JSON.parse(script.textContent.trim());
    } catch {
        return;
    }

    const selected = [];

    selector.querySelectorAll('[data-option-position]').forEach(el => {
        const pos = parseInt(el.dataset.optionPosition, 10) - 1;
        if (el.type === 'radio' && el.checked) selected[pos] = el.value;
        if (el.tagName === 'SELECT') selected[pos] = el.value;
    });

    if (!selected[0]) return; // option1 is required

    const variant = variants.find(v =>
        v.option1 === selected[0] &&
        (selected[1] == null || v.option2 === selected[1]) &&
        (selected[2] == null || v.option3 === selected[2])
    );
    if (!variant) return;

    const btn = card.querySelector('.rev-atc-button');
    if (btn) {
        btn.dataset.variantId = variant.id;
        btn.disabled = !variant.available;
    }

    const hidden = selector.querySelector('input[type="hidden"]');
    if (hidden) hidden.value = variant.id;

    // Rerender price and media (optional)
    const targets = ['.rev-price-wrapper'];
    const updateMedia = selector.dataset.updateMedia === 'true';
    if (updateMedia) targets.push('.rev-media');
    RevRerender.rerender({
        contextEl: card,
        targets,
        incomingScopeSelector: `[data-rev-key="${CSS.escape(card.dataset.revKey)}"]`,
        url: () => {
            const sectionHost = card.closest('[data-section-id]');
            const sectionId = sectionHost?.dataset.sectionId;
            const productUrl = card.dataset.productUrl;
            if (!sectionId || !productUrl) return null;
            const u = new URL(productUrl, window.location.origin);
            u.searchParams.set('section_id', sectionId);
            u.searchParams.set('variant', variant.id);
            return u.toString();
        }
    });
});

// ================================
// Open Cart Drawer - Theme Agnostic
// ================================
function revOpenCartDrawer({ fallbackToCartPage = false } = {}) {
    // 1) Dawn/OS2 cart drawer custom element (common)
    const drawerEl =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-drawer-drawer') ||
        document.querySelector('#CartDrawer') ||
        document.querySelector('[id*="CartDrawer"]');

    // If the element has an open/show method, try it.
    try {
        if (drawerEl?.open) { drawerEl.open(); return true; }
        if (drawerEl?.show) { drawerEl.show(); return true; }
    } catch (_) { }

    // 2) Click common cart-toggle triggers (most themes wire these)
    const triggers = [
        '[aria-controls="CartDrawer"]',
        '[aria-controls="cart-drawer"]',
        '[data-cart-toggle]',
        '[data-drawer-trigger="cart"]',
        'a[href="/cart"][role="button"]',
        'button[name="cart"]',
        '.header__icon--cart',
        '.site-header__cart',
        '.cart-link',
    ];

    for (const sel of triggers) {
        const el = document.querySelector(sel);
        if (!el) continue;
        // Dispatch a real click sequence
        el.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true }));
        el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        el.click();
        return true;
    }

    // 3) Last resort: go to cart page (optional)
    if (fallbackToCartPage) window.location.href = '/cart';
    return false;
}

// ================================
// Triggered Popups (singleton engine)
// ================================
(function () {
    if (window.__REV_POPUP_ENGINE__) return; // prevent double-init
    window.__REV_POPUP_ENGINE__ = true;

    const instances = new Map();
    let globalListenersBound = false;

    function parseConfig(overlay) {
        const cfgEl = overlay.querySelector('.rev-popup-config');
        if (!cfgEl) return null;
        try {
            const cfg = JSON.parse(cfgEl.textContent.trim());
            if (!cfg.sectionId) return null;
            return cfg;
        } catch {
            return null;
        }
    }

    function makeStorageKeys(sectionId) {
        return {
            popupShown: `revPopupShown_${sectionId}`,
            pageviews: `revPopupPageviews_${sectionId}`,
            visitorFirstSeen: 'revPopupFirstVisit',
            trafficSource: 'revPopupTrafficSource'
        };
    }

    function getTrafficSource(cfg) {
        const keys = makeStorageKeys(cfg.sectionId);
        const stored = localStorage.getItem(keys.trafficSource);
        if (stored) return stored;

        const params = new URLSearchParams(window.location.search);
        const utm_source = (params.get('utm_source') || '').toLowerCase();
        const utm_medium = (params.get('utm_medium') || '').toLowerCase();
        const referrer = (document.referrer || '').toLowerCase();

        let source = 'unknown';

        if (utm_source || utm_medium) {
            if (utm_source.includes('facebook') || utm_source.includes('instagram') || utm_source.includes('fb') || utm_source.includes('ig')) source = 'meta';
            else if (utm_source.includes('google') || utm_medium.includes('cpc') || utm_medium.includes('ppc')) source = 'google';
            else if (utm_source.includes('klaviyo') || utm_source.includes('email')) source = 'klaviyo';
            else if (utm_source.includes('tiktok')) source = 'tiktok';
            else if (utm_source.includes('snapchat') || utm_source.includes('snap')) source = 'snapchat';
        }

        if (source === 'unknown') {
            if (!referrer) source = 'direct';
            else if (referrer.includes('facebook.com') || referrer.includes('instagram.com')) source = 'meta';
            else if (referrer.includes('google.com') || referrer.includes('google.')) source = 'google';
            else if (referrer.includes('tiktok.com')) source = 'tiktok';
            else if (referrer.includes('snapchat.com')) source = 'snapchat';
        }

        localStorage.setItem(keys.trafficSource, source);
        return source;
    }

    function passesFilters(inst) {
        const cfg = inst.config;
        if (!cfg.filteringEnabled) return true;

        if (cfg.filterDevice && cfg.filterDevice !== 'all') {
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (cfg.filterDevice === 'mobile' && !isMobile) return false;
            if (cfg.filterDevice === 'desktop' && isMobile) return false;
        }

        if (cfg.filterVisitorStatus && cfg.filterVisitorStatus !== 'all') {
            const keys = inst.storageKeys;
            const firstVisit = localStorage.getItem(keys.visitorFirstSeen);
            const isNew = !firstVisit;
            if (!firstVisit) localStorage.setItem(keys.visitorFirstSeen, Date.now().toString());

            if (cfg.filterVisitorStatus === 'new' && !isNew) return false;
            if (cfg.filterVisitorStatus === 'returning' && isNew) return false;
        }

        if (cfg.filterTrafficSource) {
            const source = getTrafficSource(cfg);
            const allowed = [];
            if (cfg.filterTrafficUnknown) allowed.push('unknown');
            if (cfg.filterTrafficDirect) allowed.push('direct');
            if (cfg.filterTrafficMeta) allowed.push('meta');
            if (cfg.filterTrafficGoogle) allowed.push('google');
            if (cfg.filterTrafficKlaviyo) allowed.push('klaviyo');
            if (cfg.filterTrafficTikTok) allowed.push('tiktok');
            if (cfg.filterTrafficSnapchat) allowed.push('snapchat');
            if (allowed.length && !allowed.includes(source)) return false;
        }

        return true;
    }

    async function getCartTotalCents() {
        try {
            const res = await fetch('/cart.js');
            const cart = await res.json();
            return cart.total_price || 0;
        } catch {
            return 0;
        }
    }

    function hasBeenShown(inst) {
        if (!inst.config.showOnce) return false;
        return localStorage.getItem(inst.storageKeys.popupShown) === 'true';
    }

    function markShown(inst) {
        if (!inst.config.showOnce) return;
        localStorage.setItem(inst.storageKeys.popupShown, 'true');
    }

    function log(inst, msg, data) {
        if (inst.config.enableConsoleLog) {
            console.log(`[RevFlare Popup ${inst.config.sectionId}] ${msg}`, data || '');
        }
    }

    function show(inst, force = false) {
        if (!force && hasBeenShown(inst)) return;
        const overlay = inst.overlay;
        if (!overlay) return;

        overlay.style.display = 'flex';
        void overlay.offsetWidth;

        // If AB test css hide the popup (e.g. display:none !important) it remains hidden
        const cs = window.getComputedStyle(overlay);
        if (cs.display === 'none' || cs.visibility === 'hidden') {
            log(inst, 'Popup suppressed by CSS (A/B test). Skipping show.');
            overlay.style.display = 'none';
            return;
        }

        const delay = force ? 0 : (inst.config.displayDelay || 0);

        window.setTimeout(() => {
            overlay.classList.add('active');
            document.body.style.overflow = 'hidden';

            // Re-check after activation in case CSS hides it when .active is applied
            const cs2 = window.getComputedStyle(overlay);
            if (cs2.display === 'none' || cs2.visibility === 'hidden') {
                log(inst, 'Popup suppressed after activation. Reverting.');
                overlay.classList.remove('active');
                overlay.style.display = 'none';

                const anyOpen = document.querySelectorAll('.rev-popup-overlay.active').length > 0;
                if (!anyOpen) document.body.style.overflow = '';
                return;
            }

            if (!force) markShown(inst);
            log(inst, 'Popup displayed', force ? '(forced)' : '');
        }, delay);
    }


    function hide(inst) {
        const overlay = inst.overlay;
        if (!overlay) return;

        overlay.classList.add('closing');
        overlay.classList.remove('active');

        window.setTimeout(() => {
            overlay.classList.remove('closing');
            overlay.style.display = 'none';

            const anyOpen = document.querySelectorAll('.rev-popup-overlay.active').length > 0;
            if (!anyOpen) document.body.style.overflow = '';
        }, 600);
    }

    async function checkAndShow(inst, triggerSource) {
        log(inst, 'Checking popup conditions', { triggerSource });

        if (hasBeenShown(inst)) return;
        if (!passesFilters(inst)) return;

        if (triggerSource === 'atc' && inst.config.triggerATC) {
            const total = await getCartTotalCents();
            if (total < (inst.config.cartThreshold || 0)) return;
        }

        show(inst, false);
    }

    function bindPerInstanceUI(inst) {
        const sectionId = inst.config.sectionId;

        const closeBtn = inst.overlay.querySelector(`#revPopupClose-${CSS.escape(sectionId)}`);
        if (closeBtn) closeBtn.addEventListener('click', () => hide(inst));

        inst.overlay.addEventListener('click', (e) => {
            if (e.target === inst.overlay) hide(inst);
        });

        inst.overlay.querySelectorAll('.rev-atc-button').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const variantId = btn.getAttribute('data-variant-id');
                if (!variantId) return;

                const originalAria = btn.getAttribute('aria-label') || '';
                try {
                    btn.disabled = true;
                    btn.classList.add('loading');
                    btn.setAttribute('aria-busy', 'true');
                    btn.setAttribute('aria-label', 'Adding to cart');

                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
                    });
                    if (!res.ok) throw new Error('Failed to add');

                    document.body.classList.add('rev-popupsell-added');
                    document.documentElement.dispatchEvent(
                        new CustomEvent('cart:refresh', { bubbles: true })
                    );

                    window.setTimeout(() => {
                        revOpenCartDrawer({ fallbackToCartPage: false });
                    }, 50);

                    window.setTimeout(() => hide(inst), 800);

                } catch (err) {
                    log(inst, 'Error adding to cart', err);
                } finally {
                    btn.classList.remove('loading');
                    btn.disabled = false;
                    btn.removeAttribute('aria-busy');
                    btn.setAttribute('aria-label', originalAria);
                }
            });
        });
    }

    function registerAll() {
        document.querySelectorAll('.rev-popup-overlay[data-section-id]').forEach((overlay) => {
            const cfg = parseConfig(overlay);
            if (!cfg) return;

            const sectionId = cfg.sectionId;
            if (instances.has(sectionId)) return;

            const inst = {
                overlay,
                config: cfg,
                storageKeys: makeStorageKeys(sectionId),
                state: {
                    scrollTriggered: false,
                    exitIntentTriggered: false,
                    timeTriggered: false
                }
            };

            instances.set(sectionId, inst);
            bindPerInstanceUI(inst);

            // per-instance triggers (not gloabl)
            if (cfg.triggerPageview) {
                const key = inst.storageKeys.pageviews;
                const count = (parseInt(localStorage.getItem(key) || '0', 10) || 0) + 1;
                localStorage.setItem(key, String(count));
                if (count >= (cfg.pageviewThreshold || 0)) checkAndShow(inst, 'pageview');
            }

            if (cfg.triggerTime && (cfg.timeThreshold || 0) > 0) {
                window.setTimeout(() => {
                    if (inst.state.timeTriggered) return;
                    inst.state.timeTriggered = true;
                    checkAndShow(inst, 'time_delay');
                }, cfg.timeThreshold);
            }
        });

        // global API
        if (!window.RevFlarePopups) window.RevFlarePopups = {};
        instances.forEach((inst, sectionId) => {
            window.RevFlarePopups[sectionId] = {
                open: () => show(inst, true),
                close: () => hide(inst),
                reset: () => {
                    localStorage.removeItem(inst.storageKeys.popupShown);
                    inst.state.scrollTriggered = false;
                    inst.state.exitIntentTriggered = false;
                    inst.state.timeTriggered = false;
                },
                resetPageviews: () => localStorage.removeItem(inst.storageKeys.pageviews),
                getSectionId: () => sectionId
            };
        });

        if (!window.RevFlarePopup) {
            window.RevFlarePopup = {
                get: (sectionId) => window.RevFlarePopups?.[sectionId],
                getAll: () => window.RevFlarePopups,
                openById: (sectionId) => window.RevFlarePopups?.[sectionId]?.open?.(),
                closeAll: () => Object.values(window.RevFlarePopups || {}).forEach(p => p.close?.()),
                resetAll: () => Object.values(window.RevFlarePopups || {}).forEach(p => p.reset?.())
            };
        }
    }

    function bindGlobalListenersOnce() {
        if (globalListenersBound) return;
        globalListenersBound = true;

        let scrollTimeout;
        window.addEventListener('scroll', () => {
            if (scrollTimeout) return;
            scrollTimeout = window.setTimeout(() => {
                const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

                instances.forEach((inst) => {
                    const cfg = inst.config;

                    if (cfg.triggerScrollDepth && !inst.state.scrollTriggered) {
                        if (scrollPercent >= (cfg.scrollDepthThreshold || 0)) {
                            inst.state.scrollTriggered = true;
                            checkAndShow(inst, 'scroll_depth');
                        }
                    }

                    if (cfg.triggerExitIntent && !inst.state.exitIntentTriggered) {
                        const isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent);
                        if (isMobile) {
                            inst._lastScrollY = inst._lastScrollY ?? window.scrollY;
                            const last = inst._lastScrollY;
                            const current = window.scrollY;
                            if (last - current > 60 && current < 150) {
                                inst.state.exitIntentTriggered = true;
                                checkAndShow(inst, 'exit_intent');
                            }
                            inst._lastScrollY = current;
                        }
                    }
                });

                scrollTimeout = null;
            }, 200);
        });

        document.addEventListener('mouseout', (e) => {
            if (!(e.clientY <= 0 && e.relatedTarget === null)) return;
            instances.forEach((inst) => {
                if (inst.config.triggerExitIntent && !inst.state.exitIntentTriggered) {
                    inst.state.exitIntentTriggered = true;
                    checkAndShow(inst, 'exit_intent');
                }
            });
        });

        document.addEventListener('cart:updated', () => {
            instances.forEach(inst => inst.config.triggerATC && checkAndShow(inst, 'atc'));
        });
        document.addEventListener('product:added', () => {
            instances.forEach(inst => inst.config.triggerATC && checkAndShow(inst, 'atc'));
        });

        const originalFetch = window.fetch;
        window.fetch = function (...args) {
            const url = args?.[0];
            return originalFetch.apply(this, args).then((res) => {
                try {
                    if (typeof url === 'string' && url.includes('/cart/add') && res && res.ok) {
                        window.setTimeout(() => {
                            instances.forEach(inst => inst.config.triggerATC && checkAndShow(inst, 'atc'));
                        }, 500);
                    }
                } catch (_) { }
                return res;
            });
        };

        if (window.jQuery && !window.__REV_POPUP_JQ_BOUND__) {
            window.__REV_POPUP_JQ_BOUND__ = true;
            window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
                if (settings?.url && settings.url.includes('/cart/add')) {
                    window.setTimeout(() => {
                        instances.forEach(inst => inst.config.triggerATC && checkAndShow(inst, 'atc'));
                    }, 500);
                }
            });
        }
    }

    function init() {
        registerAll();
        bindGlobalListenersOnce();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();